import React, { useRef } from 'react';
import { Card, getDescription } from './Card';
import type { ItemKind, StackedItemWithKind } from './Card';
import type { UniqueItem } from '../api';
import { useMonsterTokenStyle } from '../utils/clientSettings';

export const getItemKind = (id: string): ItemKind => {
  switch (id) {
    case 'uniqueItems':
    case 'exhibits':
      return 'unique'
    case 'commonItems':
      return 'common';
    case 'spells':
      return 'spell';
    case 'skills':
      return 'skill';
    case 'allies':
      return 'ally';
    default:
      return 'ancientOne';
  }
}

export const getItemKindFromCardType = (cardType?: string | null | undefined): ItemKind | 'investigator' | null => {
  switch (cardType) {
    case 'UniqueItem':
      return 'unique'
    case 'CommonItem':
      return 'common';
    case 'Spell':
      return 'spell';
    case 'Skill':
      return 'skill';
    case 'Ally':
      return 'ally';
    case 'Madness':
      return 'madness';
    case 'Injury':
      return 'injury';
    case 'Investigator':
      return 'investigator';
    case 'Special':
    case 'SpecialCard':
      return 'special';
    case 'Corruption':
      return 'corruption';
    case 'Gate':
      return 'gate';
    case 'Relationship':
      return 'relationship';
    case 'PersonalStory':
      return 'personalStory';
    default:
      return null;
  }
}

export type { ItemKind, StackedItem, StackedItemWithKind } from './Card';

interface StackedItemCardProps {
  entry: StackedItemWithKind;
  zIndex: number;
  isUsed?: boolean;
}

export const StackedItemCard: React.FC<StackedItemCardProps> = ({ entry, zIndex, isUsed = false }) => {
  const artwork = useMonsterTokenStyle();
  const digital = artwork === 'Digital' && ['common', 'unique', 'spell', 'ally', 'skill', 'personalStory', 'relationship', 'madness', 'injury', 'special', 'corruption'].includes(entry.kind);
  const selectInsteadOfInspect = useRef(false);
  const displayTitle = entry.item.title?.trim() || entry.item.name;
  const description = getDescription(entry);
  const storedMonsters = entry.kind === 'unique'
    ? ((entry.item as UniqueItem).storedMonsters ?? [])
    : [];
  const storedMonsterDescription = storedMonsters.length > 0
    ? `\nStored monsters: ${storedMonsters.map(monster => {
      const toughness = monster.toughnessBreakdown?.total ?? monster.Toughness ?? monster.toughness;
      return `${monster.name} (Toughness ${toughness})`;
    }).join(', ')}`
    : '';
  const tooltip = `${isUsed ? `${displayTitle} (used)` : displayTitle}${description ? `\n${description}` : ''}${storedMonsterDescription}`;

  return (
    <div
      className={`stacked-card${isUsed ? ' used' : ''}`}
      title={tooltip}
      style={{ zIndex }}
      tabIndex={digital ? 0 : undefined}
      role={digital ? 'group' : undefined}
      aria-label={digital ? `${displayTitle} card` : undefined}
      onPointerDownCapture={(e) => {
        const control = (e.target as Element).closest('button, a, input, select, textarea');
        const bounds = control?.getBoundingClientRect();
        // Mobile browsers can redirect a tap near a small, rotated control to it.
        // Keep taps outside its actual bounds as card selection, using the original
        // pointer coordinates before the browser adjusts the subsequent click.
        selectInsteadOfInspect.current = digital && e.pointerType === 'touch' && bounds != null
          && (e.clientX < bounds.left || e.clientX > bounds.right || e.clientY < bounds.top || e.clientY > bounds.bottom);
        if (digital && (!control || selectInsteadOfInspect.current)) {
          // Focus rotates used cards; defer it until the selecting click completes.
          e.preventDefault();
        }
      }}
      onPointerCancel={() => { selectInsteadOfInspect.current = false; }}
      onKeyDownCapture={() => { selectInsteadOfInspect.current = false; }}
      onClickCapture={(e) => {
        if (selectInsteadOfInspect.current) {
          selectInsteadOfInspect.current = false;
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.focus({ preventScroll: true });
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (digital && !(e.target as HTMLElement).closest('button, a, input, select, textarea')) {
          e.currentTarget.focus({ preventScroll: true });
        }
      }}
    >
      <Card card={entry} />
      {storedMonsters.length > 0 && (
        <span className="stored-monsters-badge" aria-label={`${storedMonsters.length} stored monsters`}>
          {storedMonsters.length}
        </span>
      )}
    </div>
  );
};
